/**
 * sw-cookie-consent.js — Swiss Watches Dot Com cookie banner + preferences modal.
 *
 * Built on Shopify's Customer Privacy API (consent-tracking-api 0.1):
 *   window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], cb)
 *   window.Shopify.customerPrivacy.{ shouldShowBanner, currentVisitorConsent, setTrackingConsent, ... }
 *
 * - Banner shows only when shouldShowBanner() is true; hides after any decision.
 * - Honors Global Privacy Control (navigator.globalPrivacyControl === true):
 *   marketing + sale_of_data are set to false and persisted; a note is shown.
 * - Never sets tracking cookies itself; consent state lives in Shopify's API.
 * - Public API: window.SwConsent = { open(), accept(), decline(), get() }
 * - Event: document 'sw:consent:changed' (detail = { analytics, marketing, preferences, sale_of_data }).
 * - Any [data-sw-consent-open] click anywhere on the page opens the preferences modal.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-sw-cookie-root]');
  if (!root || window.SwConsent) return;

  var banner = root.querySelector('[data-sw-cookie-banner]');
  var modal = root.querySelector('[data-sw-cookie-modal]');
  var panel = modal ? modal.querySelector('[data-sw-cookie-panel]') : null;
  var gpcNotes = root.querySelectorAll('[data-sw-cookie-gpc]');
  var errorNotes = root.querySelectorAll('[data-sw-cookie-error]');
  var prefInputs = {};
  Array.prototype.forEach.call(root.querySelectorAll('[data-sw-cookie-pref]'), function (input) {
    prefInputs[input.getAttribute('data-sw-cookie-pref')] = input;
  });

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var DURATION = 360;
  var gpc = navigator.globalPrivacyControl === true;
  var api = null;
  var apiFailed = false;
  var loading = false;
  var waiters = [];
  var lastFocus = null;
  var hideTimer = null;
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* Customer Privacy API                                                 */
  /* ------------------------------------------------------------------ */
  function withApi(cb) {
    if (api) return cb(api);
    if (apiFailed) return;
    waiters.push(cb);
    if (loading) return;
    loading = true;

    var tries = 0;
    (function attempt() {
      var shopify = window.Shopify;
      if (shopify && typeof shopify.loadFeatures === 'function') {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
          if (error || !window.Shopify.customerPrivacy) {
            apiFailed = true;
            waiters = [];
            if (window.console && console.warn) console.warn('[sw-cookie-consent] Customer Privacy API unavailable', error);
            return;
          }
          api = window.Shopify.customerPrivacy;
          var pending = waiters.slice();
          waiters = [];
          pending.forEach(function (fn) { fn(api); });
        });
        return;
      }
      if (tries++ < 40) {
        setTimeout(attempt, 250);
      } else {
        apiFailed = true;
        waiters = [];
      }
    })();
  }

  function rawConsent(cp) {
    try {
      return cp.currentVisitorConsent() || {};
    } catch (e) {
      return {};
    }
  }

  function hasDecision(raw) {
    return ['analytics', 'marketing', 'preferences', 'sale_of_data'].some(function (key) {
      return raw[key] === 'yes' || raw[key] === 'no';
    });
  }

  /** Effective state per category: the visitor's explicit choice, else what the region currently allows. */
  function effective(cp) {
    var raw = rawConsent(cp);
    function pick(key, allowedFn) {
      if (raw[key] === 'yes') return true;
      if (raw[key] === 'no') return false;
      try {
        return typeof cp[allowedFn] === 'function' ? !!cp[allowedFn]() : false;
      } catch (e) {
        return false;
      }
    }
    return {
      analytics: pick('analytics', 'analyticsProcessingAllowed'),
      marketing: pick('marketing', 'marketingAllowed'),
      preferences: pick('preferences', 'preferencesProcessingAllowed'),
      sale_of_data: pick('sale_of_data', 'saleOfDataAllowed')
    };
  }

  function normalize(prefs) {
    return {
      analytics: !!prefs.analytics,
      marketing: !!prefs.marketing,
      preferences: !!prefs.preferences,
      sale_of_data: !!prefs.sale_of_data
    };
  }

  function setConsent(prefs, options) {
    options = options || {};
    var consent = normalize(prefs);
    withApi(function (cp) {
      cp.setTrackingConsent(consent, function (result) {
        if (result && result.error) {
          if (window.console && console.warn) console.warn('[sw-cookie-consent] setTrackingConsent failed', result.error);
          if (!options.silent) toggleErrors(true);
          return;
        }
        toggleErrors(false);
        if (!options.silent) {
          hideBanner();
          closeModal();
        }
        syncInputs();
        document.dispatchEvent(new CustomEvent('sw:consent:changed', { detail: consent }));
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Decisions                                                            */
  /* ------------------------------------------------------------------ */
  function accept() {
    // Under a Global Privacy Control signal the sale/sharing opt-out stays in place;
    // marketing can still be switched on explicitly in the preferences modal.
    setConsent({ analytics: true, preferences: true, marketing: !gpc, sale_of_data: !gpc });
  }

  function decline() {
    setConsent({ analytics: false, marketing: false, preferences: false, sale_of_data: false });
  }

  function savePreferences() {
    var marketing = !!(prefInputs.marketing && prefInputs.marketing.checked);
    setConsent({
      preferences: !!(prefInputs.preferences && prefInputs.preferences.checked),
      analytics: !!(prefInputs.analytics && prefInputs.analytics.checked),
      marketing: marketing,
      sale_of_data: marketing
    });
  }

  function syncInputs() {
    if (!api) return;
    var state = effective(api);
    Object.keys(prefInputs).forEach(function (key) {
      prefInputs[key].checked = !!state[key];
    });
  }

  function showGpcNotes() {
    Array.prototype.forEach.call(gpcNotes, function (node) {
      node.hidden = false;
    });
  }

  function toggleErrors(show) {
    Array.prototype.forEach.call(errorNotes, function (node) {
      node.hidden = !show;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Banner                                                               */
  /* ------------------------------------------------------------------ */
  function showBanner() {
    if (!banner) return;
    clearTimeout(hideTimer);
    banner.hidden = false;
    // Force a frame so the transition runs from the hidden state.
    void banner.offsetWidth;
    banner.classList.add('is-visible');
  }

  function hideBanner() {
    if (!banner || banner.hidden) return;
    banner.classList.remove('is-visible');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      banner.hidden = true;
    }, reducedMotion ? 0 : DURATION);
  }

  /* ------------------------------------------------------------------ */
  /* Modal                                                                */
  /* ------------------------------------------------------------------ */
  function focusables() {
    return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function onModalKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeModal();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onFocusIn(event) {
    if (!modal || modal.hidden) return;
    if (!panel.contains(event.target)) {
      panel.focus({ preventScroll: true });
    }
  }

  function openModal(trigger) {
    if (!modal || !panel) return;
    lastFocus = trigger && typeof trigger.focus === 'function' ? trigger : document.activeElement;
    withApi(function () {
      syncInputs();
    });
    toggleErrors(false);
    modal.hidden = false;
    void modal.offsetWidth;
    modal.classList.add('is-open');
    document.documentElement.classList.add('sw-cookie-lock');
    document.addEventListener('keydown', onModalKeydown);
    document.addEventListener('focusin', onFocusIn);
    panel.focus({ preventScroll: true });
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('sw-cookie-lock');
    document.removeEventListener('keydown', onModalKeydown);
    document.removeEventListener('focusin', onFocusIn);
    var returnTo = lastFocus;
    lastFocus = null;
    var finish = function () {
      modal.hidden = true;
      if (returnTo && document.contains(returnTo) && typeof returnTo.focus === 'function' && !(banner && banner.contains(returnTo) && banner.hidden)) {
        returnTo.focus({ preventScroll: true });
      }
    };
    if (reducedMotion) {
      finish();
    } else {
      setTimeout(finish, DURATION);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                               */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var opener = target.closest('[data-sw-consent-open]');
    if (opener) {
      event.preventDefault();
      openModal(opener);
      return;
    }
    if (!root.contains(target)) return;

    if (target.closest('[data-sw-cookie-accept]')) {
      event.preventDefault();
      accept();
    } else if (target.closest('[data-sw-cookie-decline]')) {
      event.preventDefault();
      decline();
    } else if (target.closest('[data-sw-cookie-save]')) {
      event.preventDefault();
      savePreferences();
    } else if (target.closest('[data-sw-cookie-close]')) {
      event.preventDefault();
      closeModal();
    }
  });

  /* ------------------------------------------------------------------ */
  /* Init                                                                 */
  /* ------------------------------------------------------------------ */
  withApi(function (cp) {
    var raw = rawConsent(cp);
    var shouldShow = false;
    try {
      shouldShow = !!cp.shouldShowBanner();
    } catch (e) {
      shouldShow = false;
    }

    if (gpc) {
      showGpcNotes();
      var alreadyOptedOut = raw.sale_of_data === 'no' && raw.marketing === 'no';
      if (!alreadyOptedOut) {
        var state = effective(cp);
        // Keep analytics/preferences as the visitor chose (or the region default), opt out of sale/sharing.
        setConsent(
          { analytics: state.analytics, preferences: state.preferences, marketing: false, sale_of_data: false },
          { silent: true }
        );
      }
    }

    if (shouldShow) {
      showBanner();
    } else if (hasDecision(raw)) {
      hideBanner();
    }
    syncInputs();
  });

  window.SwConsent = {
    open: function () { openModal(); },
    accept: accept,
    decline: decline,
    get: function () {
      return api ? api.currentVisitorConsent() : null;
    }
  };
})();
